import styled, { useTheme } from 'styled-components';
import Modal from '../../../components/Modal';
import {
  BodyMedium,
  Heading5Text,
  TitleSmall,
} from '../../../components/Texts';
import { formatUSDEnsure2Digits } from '../../../common/currency';
import Button from '../../../components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { selectTokensWithUSDBalance } from '../../../redux/slices/tokenSlice';
import { selectTokenByNetworkAndSymbol } from '../../../redux/slices/tokenSlice';
import { useMemo } from 'react';
import { useLazyGetSwapQuoteQuery } from '../../../redux/api/authorized/swap';
import { toTokenAmount } from '../../../hooks/useSolana';
import { openModal } from '../../../redux/slices/modals';
import { hapticsImpactLight } from '../../../common/haptic';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';

const PaymentOptionContainer = styled.div`
  background-color: ${({ theme }) => theme.background.secondary};
  border-radius: 16px;
  padding: 16px;
  gap: 12px;
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }) => theme.background.secondPrimary};
    }
  }
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const TokenLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
`;

const AmountInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const TokenScroll = styled.div`
  max-height: calc(100vh - 385px);
  width: 100%;
  overflow-y: auto;
`;

const PaymentSuggestionModal = ({
  onClose,
  amount,
  targetToken,
  onPaymentMethodSelected,
  tokenOptions,
}) => {
  const dispatch = useDispatch();
  // Get all tokens with non-zero balance for this network

  // Get USDC token for swap quotes
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'USDC'),
  );

  const [triggerGetSwapQuote] = useLazyGetSwapQuoteQuery();

  // Create payment options from all available tokens
  const paymentOptions = useMemo(() => {
    return tokenOptions.map((token) => ({
      token,
      balance: token.balance,
      balanceUSD: token.usdValue,
    }));
  }, [tokenOptions, targetToken]);

  if (paymentOptions.length === 0) {
    dispatch(openModal({ name: 'addFunds', props: { amount: amount } }));
    return;
  }

  const handlePaymentSelect = async (option) => {
    try {
      // Get swap quote from USDC to the selected token
      hapticsImpactLight();
      const swapQuote = await triggerGetSwapQuote({
        inputMint: usdcToken.address, // USDC
        outputMint: option.token.address, // The token the user selected
        amount: toTokenAmount(amount, usdcToken.decimals), // USD amount in USDC units
        slippageBps: 'auto',
        calculateFees: true,
        isGift: true,
      }).unwrap();

      if (swapQuote?.quote) {
        // Use the outAmount to get exactly how much of the selected token is needed
        const exactAmountNeeded = swapQuote.quote.outAmount; // This is already in raw token units
        const paymentData = {
          token: option.token,
          balance: option.balance,
          balanceUSD: option.balanceUSD,
          exactAmountNeeded: exactAmountNeeded,
          swapQuote: swapQuote,
        };

        onPaymentMethodSelected(paymentData);
      } else {
        // Fallback if no quote available - use simple price calculation
        const fallbackAmountHuman = amount / (option.token.usdPrice || 1);
        const fallbackAmountRaw = toTokenAmount(
          fallbackAmountHuman.toString(),
          option.token.decimals,
        );
        const paymentData = {
          token: option.token,
          balance: option.balance,
          balanceUSD: option.balanceUSD,
          exactAmountNeeded: fallbackAmountRaw,
          swapQuote: null,
        };

        onPaymentMethodSelected(paymentData);
      }
    } catch (error) {
      console.error('Error getting swap quote:', error);
      // Fallback to simple price calculation
      const fallbackAmountHuman = amount / (option.token.usdPrice || 1);
      const fallbackAmountRaw = toTokenAmount(
        fallbackAmountHuman.toString(),
        option.token.decimals,
      );
      const paymentData = {
        token: option.token,
        balance: option.balance,
        balanceUSD: option.balanceUSD,
        exactAmountNeeded: fallbackAmountRaw,
        swapQuote: null,
      };

      onPaymentMethodSelected(paymentData);
      onClose();
    }
  };

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const theme = useTheme();

  return (
    <Modal onClose={onClose} width={!isMobile ? '600px' : '100%'}>
      <div
        style={{
          display: 'flex',
          gap: 24,
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Heading5Text
            style={{
              fontWeight: '600',
              marginTop: 20,
              color: theme.text.title,
            }}
          >
            Payment Method
          </Heading5Text>
        </div>
        <TokenScroll>
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexDirection: 'column',
              width: '100%',
            }}
          >
            {paymentOptions.map((option, index) => (
              <PaymentOptionContainer
                key={index}
                onClick={() => handlePaymentSelect(option)}
              >
                <TokenLogo
                  src={option.token.logoURI}
                  alt={option.token.symbol}
                />
                <TokenInfo>
                  <TitleSmall>
                    {option.token.name}{' '}
                    <span
                      style={{
                        color: `${theme.text.secondary}`,
                        fontWeight: 300,
                        fontSize: 12,
                      }}
                    >
                      ({option.token.symbol})
                    </span>
                  </TitleSmall>
                  <BodyMedium
                    style={{
                      color: `${theme.text.secondary}`,
                      fontSize: 14,
                    }}
                  >
                    {option.description}
                  </BodyMedium>
                </TokenInfo>
                <AmountInfo>
                  <BodyMedium style={{ fontWeight: 600 }}>
                    {formatUSDEnsure2Digits(option.balanceUSD)}
                  </BodyMedium>
                  <BodyMedium
                    style={{
                      color: `${theme.text.secondary}`,
                      fontSize: 12,
                    }}
                  >
                    Available
                  </BodyMedium>
                </AmountInfo>
              </PaymentOptionContainer>
            ))}
          </div>
        </TokenScroll>
        <div style={{ marginLeft: 'auto' }}>
          <Button variant='white' onClick={onClose}>
            CANCEL
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PaymentSuggestionModal;
